import type { HttpError } from '@refinedev/core'
import { useSelect } from '@refinedev/core'
import { useTable } from '@refinedev/react-table'
import type { PlatformUser, Tenant } from '@sirius/contracts/admin'
import type { ColumnDef } from '@tanstack/react-table'
import { useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { ListEmptyState } from '~/components/listEmptyState'
import { ShowButton } from '~/components/refineUi/buttons/show'
import { DataTable } from '~/components/refineUi/dataTable/dataTable'
import {
  DataTableFilterCombobox,
  DataTableFilterDropdownText,
} from '~/components/refineUi/dataTable/dataTableFilter'
import { DataTableSorter } from '~/components/refineUi/dataTable/dataTableSorter'
import { ListView, ListViewHeader } from '~/components/refineUi/views/listView'
import { formatDateTime } from '~/i18n/format'
import { CreateUserDialog } from './createUserDialog'
import { bannedOptions, membershipsText, roleOptions } from './userFormat'
import { UserRoleBadge, UserStatusBadge } from './userParts'

const dateTime = (value: Date) => formatDateTime(value)

/**
 * Every account, platform roles and tenant users, across tenants, and
 * Create user: platform admins only.
 */
export function UserList() {
  const { t } = useTranslation()
  const tenantOptions = useSelect<Tenant, HttpError>({
    resource: 'tenants',
    optionLabel: 'name',
    optionValue: 'id',
    pagination: { mode: 'off' },
    sorters: [{ field: 'name', order: 'asc' }],
  })
  const tenantFilterOptions = useMemo(
    () =>
      tenantOptions.options.map(({ label, value }) => ({
        label,
        value: String(value),
      })),
    [tenantOptions.options],
  )

  const columns = useMemo<ColumnDef<PlatformUser>[]>(
    () => [
      {
        id: 'name',
        size: 130,
        accessorKey: 'name',
        header: ({ column, table }) => (
          <div className="flex items-center gap-1">
            {t('usersPage.list.columns.name')}
            <DataTableSorter column={column} />
            <DataTableFilterDropdownText
              defaultOperator="contains"
              operators={['contains']}
              column={column}
              table={table}
              placeholder={t('usersPage.list.searchName')}
            />
          </div>
        ),
      },
      {
        id: 'email',
        size: 190,
        accessorKey: 'email',
        header: ({ column, table }) => (
          <div className="flex items-center gap-1">
            {t('usersPage.list.columns.email')}
            <DataTableSorter column={column} />
            <DataTableFilterDropdownText
              defaultOperator="contains"
              operators={['contains']}
              column={column}
              table={table}
              placeholder={t('usersPage.list.searchEmail')}
            />
          </div>
        ),
      },
      {
        id: 'platformRole',
        size: 200,
        accessorKey: 'platformRole',
        header: ({ column, table }) => (
          <div className="flex items-center gap-1">
            {t('usersPage.list.columns.role')}
            <DataTableFilterCombobox
              column={column}
              table={table}
              options={roleOptions(t)}
              defaultOperator="in"
              operators={['in']}
              multiple
            />
          </div>
        ),
        cell: ({ getValue }) => (
          <UserRoleBadge
            platformRole={getValue<PlatformUser['platformRole']>()}
          />
        ),
        meta: { filterOperator: 'in' },
        enableSorting: false,
      },
      {
        // the filter is a membership in one of the tenants
        id: 'tenantId',
        size: 190,
        accessorKey: 'memberships',
        header: ({ column, table }) => (
          <div className="flex items-center gap-1">
            {t('usersPage.list.columns.tenants')}
            <DataTableFilterCombobox
              column={column}
              table={table}
              options={tenantFilterOptions}
              defaultOperator="in"
              operators={['in']}
              multiple
            />
          </div>
        ),
        cell: ({ getValue }) => {
          const text = membershipsText(
            getValue<PlatformUser['memberships']>(),
            t,
          )
          // the whole list on hover: a long one is cut off
          return <span title={text}>{text}</span>
        },
        meta: { filterOperator: 'in' },
        enableSorting: false,
      },
      {
        id: 'banned',
        size: 90,
        accessorKey: 'banned',
        header: ({ column, table }) => (
          <div className="flex items-center gap-1">
            {t('usersPage.list.columns.status')}
            <DataTableFilterCombobox
              column={column}
              table={table}
              options={bannedOptions(t)}
              defaultOperator="in"
              operators={['in']}
              multiple
            />
          </div>
        ),
        cell: ({ getValue }) => (
          <UserStatusBadge banned={getValue<boolean>()} />
        ),
        meta: { filterOperator: 'in' },
        enableSorting: false,
      },
      {
        id: 'lastSignInAt',
        size: 170,
        accessorKey: 'lastSignInAt',
        header: ({ column }) => (
          <div className="flex items-center gap-1">
            {t('usersPage.list.columns.lastSignIn')}
            <DataTableSorter column={column} />
          </div>
        ),
        cell: ({ getValue }) => {
          const lastSignInAt = getValue<Date | null>()
          return lastSignInAt === null
            ? t('usersPage.never')
            : dateTime(lastSignInAt)
        },
        enableColumnFilter: false,
      },
      {
        id: 'actions',
        header: '',
        size: 100,
        enableSorting: false,
        enableColumnFilter: false,
        cell: ({ row }) => (
          <div className="flex justify-end gap-2">
            <ShowButton
              recordItemId={row.original.id}
              size="sm"
            />
          </div>
        ),
      },
    ],
    [tenantFilterOptions, t],
  )

  const table = useTable<PlatformUser, HttpError>({
    columns,
    refineCoreProps: {
      resource: 'users',
      syncWithLocation: true,
      pagination: { pageSize: 20 },
      sorters: { initial: [{ field: 'name', order: 'asc' }] },
    },
  })
  const { tableQuery, filters } = table.refineCore
  const isEmpty = tableQuery.data?.total === 0

  return (
    <ListView>
      <ListViewHeader
        title={t('users.users')}
        actions={<CreateUserDialog />}
      />
      {isEmpty ? (
        <ListEmptyState
          resource="users"
          emptyText={t('usersPage.list.empty')}
          noMatchText={t('usersPage.list.noMatch')}
          canCreate={false}
          hasFilters={filters.length > 0}
          // via the column filters, so react-table and Refine stay in sync
          onClearFilters={() => table.reactTable.setColumnFilters([])}
        />
      ) : (
        <DataTable table={table} />
      )}
    </ListView>
  )
}
